import { Component, DestroyRef, inject, Input, OnInit, Renderer2 } from '@angular/core';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { ActionGetAgentStepSensitiveDataResponseModel } from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { Label } from 'src/app/labels/englishLabels';
import { HttpErrorResponse } from '@angular/common/http';
import {
  formatToolInvocationParams,
  formatToolInvocationResult,
  prettyMessageAsStringAsync,
  SupportedFormats,
} from 'src/app/common/prettyUtils';
import { Observable } from 'rxjs/internal/Observable';
import { CopyButtonComponent, PopupPosition } from '../copy-button/copy-button.component';
import { BehaviorSubject, combineLatest, EMPTY, map, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { StripHtmlPipe } from 'src/app/pipes/strip-html/strip-html.pipe';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { ActivatedRoute } from '@angular/router';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { isOutpostPlatform } from 'src/app/common/platformUtils';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';

@Component({
  selector: 'app-get-sensitive-data-input-action',
  standalone: true,
  templateUrl: './get-sensitive-data-input.component.html',
  styleUrls: ['./get-sensitive-data-input.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatSlideToggleModule,
    CopyButtonComponent,
    LabelsProviderPipe,
    MatTooltipModule,
    MatProgressSpinnerModule,
    StripHtmlPipe,
  ],
})
export class GetSensitiveDataActionComponent implements OnInit {
  @Input()
  public sensitiveDataMethod?: () => Observable<ActionGetAgentStepSensitiveDataResponseModel>;
  @Input() public showPopupBtn = true;
  @Input() public content?: string;
  @Input() public sensitiveDataIndexes?: string;

  public sensitiveData = '';
  public useInnerHtml = false;
  public showRawDataLabel = '';
  public rawSensitiveDataMode = false;
  public showRawDataSwitch = false;
  public popUpPosition = PopupPosition.Above;
  public sortedSensitivePositions: number[] = [];
  private readonly _sensitiveBlockSubject = new BehaviorSubject<boolean>(false);
  public isSensitiveBlockVisible$ = this._sensitiveBlockSubject.asObservable();
  private readonly _spinnerSubject = new BehaviorSubject<boolean>(false);
  public isSpinnerVisible$ = this._spinnerSubject.asObservable();
  public sensitiveIconToolTipLabelPath = 'aiAgentStepTranscriptDialog.sensitiveIconToolTip';
  public fileDownloadUrl?: string;
  public fileName?: string;
  private readonly _renderer = inject(Renderer2);
  private readonly _featureFlagsService = inject(FeatureFlagsService);

  public isOutpostPlatform$ = this._featureFlagsService.isFeatureEnabled(
    FeatureFlagTypes.DrOutpostFetchSensitiveDataEnabled
  )
    ? of(false)
    : combineLatest([
        this._route.queryParams,
        this._storeService.integrations$,
      ]).pipe(
        map(([params, integrations]) => {
          let integrationId = params['integration'];
          if (!integrationId && params['q']) {
            const match = params['q'].match(
              /integration:([a-f0-9-]+)/i
            );
            if (match) {
              integrationId = match[1];
            }
          }
          if (!integrationId || !integrations) return false;
          const integration = integrations.find(
            (i) => i.id === integrationId
          );
          return isOutpostPlatform(integration?.platform);
        })
      );

  constructor(
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService,
    private readonly _destroyRef: DestroyRef,
    protected readonly _permissionsService: PermissionsService,
    private readonly _route: ActivatedRoute,
    private readonly _storeService: StoreService
  ) {}

  ngOnInit(): void {
    if (!this.showPopupBtn && this.content) {
      this._sensitiveBlockSubject.next(true);
      prettyMessageAsStringAsync(this.content)
        .then((formattedTranscript) => {
          this.sensitiveData = formattedTranscript.text;
          this.useInnerHtml = this.showRawDataSwitch =
            this._isFormatSupportForInnerHtml(formattedTranscript.format) ||
            formattedTranscript.text.toLowerCase().includes('<html>') ||
            formattedTranscript.text.toLowerCase().includes('<div>');
        })
        .catch(() => {
          this.sensitiveData = this.content ? this.content : ''; // Fallback to the original transcript
        });
    }
    this.showRawDataLabel = Label.get('aiAgentStepTranscriptDialog.showRawData');
  }

  public getSensitiveData(event: MouseEvent): void {
    event.stopPropagation();
    of(null)
      .pipe(
        tap(() => {
          this._sensitiveBlockSubject.next(true);
          this._spinnerSubject.next(true);
        }),
        switchMap(() => {
          if (!this.sensitiveDataMethod) {
            console.error('sensitiveDataMethod is undefined!');
            return EMPTY;
          }
          return this.sensitiveDataMethod();
        }),
        tap(async (response: ActionGetAgentStepSensitiveDataResponseModel) => {
          // Store file download info if available
          this.fileDownloadUrl = response.fileDownloadUrl;
          this.fileName = response.title;

          let sensitiveData = '';
          if (response.transcript) {
            sensitiveData = response.transcript;
          } else if (response.payload) {
            sensitiveData = response.payload;
          } else if (response.invocationResult) {
            sensitiveData = formatToolInvocationResult(response.invocationResult);
          } else if (response.invocationParams) {
            // Activity params are wrapped: { "toolName": { actualParams } }
            // Extract and format only the inner values
            sensitiveData = Object.values(response.invocationParams)
              .map((value) => formatToolInvocationParams(value))
              .filter((result) => result !== '')
              .join('\n');
          } else if (response.snippet && (!response.singleSnippet || response.singleSnippet === 'Snippet not found')) {
            // Aggregate all snippet entries when a specific source snippet isn't resolvable
            const entries = Object.entries(response.snippet);
            if (entries.length > 0) {
              sensitiveData = entries.map(([key, value]) => `${key}:\n${value}`).join('\n\n');
            } else {
              sensitiveData = 'Snippet not found';
            }
          } else if (response.singleSnippet) {
            sensitiveData = response.singleSnippet;
          } else if (response.ragQuery) {
            sensitiveData = response.ragQuery;
          }

          if (this.sensitiveDataIndexes && sensitiveData) {
            const indexes = this.sensitiveDataIndexes.split(',').map((str) => parseInt(str.trim(), 10));
            this.sortedSensitivePositions = [...indexes]
              .filter((x) => x >= 0 && x < sensitiveData.length)
              .sort((a, b) => a - b);
          }

          await prettyMessageAsStringAsync(sensitiveData)
            .then((formattedTranscript) => {
              this.sensitiveData = formattedTranscript.text;
              this.useInnerHtml = this.showRawDataSwitch = this._isFormatSupportForInnerHtml(
                formattedTranscript.format
              );
            })
            .catch(() => {
              this.sensitiveData = sensitiveData; // Fallback to the original transcript
            });
          this.showRawDataLabel = Label.get('aiAgentStepTranscriptDialog.showRawData');
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        error: (httpError: HttpErrorResponse) => {
          if (this._httpErrorHandlerService.isUnhandledExceptionError(httpError.status)) {
            this._httpErrorHandlerService.handle(httpError);
          } else {
            this._notificationService.error(httpError.error.detail);
          }
          this._sensitiveBlockSubject.next(false);
          this._spinnerSubject.next(false);
        },
        complete: () => {
          this._spinnerSubject.next(false);
        },
      });
  }

  private _isFormatSupportForInnerHtml(format: SupportedFormats): boolean {
    return format === SupportedFormats.HTML || format === SupportedFormats.XML;
  }

  public downloadFile(): void {
    if (!this.fileDownloadUrl) {
      return;
    }
    const anchor: HTMLAnchorElement = this._renderer.createElement('a');
    anchor.href = this.fileDownloadUrl;
    anchor.download = this.fileName || 'download';
    anchor.target = '_blank';
    anchor.click();
    this._notificationService.success(Label.get('sensitiveDataInput.downloadSuccess'));
  }
}
